import { useState } from 'react'
import { format } from 'date-fns'
import { X, Copy, Check } from 'lucide-react'
import type { CalibrationRecord } from '@/types/robotEvents'
import {
  JOINT_LABELS,
  METHOD_LABELS,
  describeDelta,
  describeOrigin,
} from './calibrationFormat'

interface CalibrationDetailModalProps {
  record: CalibrationRecord
  onClose: () => void
}

// キャリブレーション 1 件の詳細。機体が書き換えた後の robot.urdf 全文を表示し、
// fork_config の vehicles/<機体>/robot.urdf へ反映するためにコピーできる
export default function CalibrationDetailModal({
  record,
  onClose,
}: CalibrationDetailModalProps) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(record.urdf)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1500)
    } catch {
      // クリップボードが使えない環境 (http 配信など) では何もしない
    }
  }

  const rows: [string, string][] = [
    ['日時', format(new Date(record.ts), 'yyyy-MM-dd HH:mm:ss')],
    ['対象', JOINT_LABELS[record.joint] ?? record.joint],
    ['方法', (METHOD_LABELS[record.method] ?? record.method) || '－'],
    ['残差', record.residual || '－'],
    ['担当', record.by || '－'],
    ['変更前', describeOrigin(record.joint, record.before)],
    ['変更後', describeOrigin(record.joint, record.after)],
    ['変更量', describeDelta(record.joint, record.before, record.after)],
    ['機体上のパス', record.path || '－'],
  ]

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <span className="text-lg font-semibold text-gray-900">
            キャリブレーション詳細
          </span>
          <button
            onClick={onClose}
            className="p-1.5 rounded hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="px-6 py-4 overflow-y-auto flex-1 min-h-0 space-y-4">
          <table className="text-sm">
            <tbody>
              {rows.map(([label, value]) => (
                <tr key={label}>
                  <td className="pr-4 py-1 text-gray-500 whitespace-nowrap align-top">
                    {label}
                  </td>
                  <td className="py-1 text-gray-900 break-all tabular-nums">
                    {value}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-gray-900">
                書き換え後の robot.urdf
              </h3>
              {record.urdf && (
                <button
                  onClick={handleCopy}
                  className="flex items-center gap-1 px-2 py-1 rounded text-xs text-gray-600 hover:bg-gray-100 transition-colors"
                  title="URDF をコピー"
                >
                  {copied ? (
                    <Check className="w-4 h-4 text-green-600" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                  {copied ? 'コピーしました' : 'コピー'}
                </button>
              )}
            </div>
            {record.urdf ? (
              <pre className="bg-gray-50 border border-gray-200 rounded p-3 text-xs text-gray-800 overflow-auto max-h-[45vh] whitespace-pre">
                {record.urdf}
              </pre>
            ) : (
              <p className="text-sm text-gray-500">
                このイベントには URDF 本文が含まれていません（古いブリッジ）
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
